<template>
    <v-dialog
        v-if="imageObject"
        :model-value="modelValue"
        width="100%"
        height="100%"
        :fullscreen="true"
        content-class="products-image-modal bg-surface"
        @update:modelValue="onClose"
    >
        <v-btn
            class="dialog-close cursor-pointer"
            variant="plain"
            @click="onClose()"
            icon
        >
            <v-icon
                color="white"
                size="x-large"
                :icon="mdiCloseCircle"
            ></v-icon>
        </v-btn>

        <!-- Main -->
        <div
            class="
                products-image-modal-container
                d-flex
                flex-column
                flex-grow-1
                w-100
                h-100
            "
            v-resize="setWidth"
        >
            <div
                class="
                    flex-shrink-0
                    flex-grow-1
                "
            >
                <div
                    ref="elProdImgModalOuter"
                    class="prod-img-modal-outer carousel-h-scroll-parent w-100 h-100"
                    :class="{
                        'overflow-hidden': isZoomed
                    }"
                >
                    <div
                        class="prod-img-modal-inner h-100 fit-w-max-content d-flex"
                    >
                        <div
                            v-for="(img, index) in productImages"
                            ref="elProdImgModalItems"
                            :id="`prod-img-${img.uid}`"
                            class="prod-img-wrapper carousel-h-scroll-item"
                            :style="{ width: modalWidth }"
                            :data-attr-val="img.uid"
                        >
                            <div
                                class="w-100 h-100 position-relative"
                            >
                                <ProductsLayeredImage
                                    class="w-100 h-100"
                                    :index="index"
                                    :image-object="img"
                                    :image-stack="imageStack"
                                    :disable-loading-indicator="index > 0"
                                    :top-image-props="{
                                        class: 'absolute-left w-100 h-100',
                                        type: 'ALL',
                                        tag: 'VImg',
                                        contain: true,
                                        disableDropShadow: !img.showDropShadow
                                    }"
                                    :low-images-container-props="{
                                        class: 'w-100 h-100'
                                    }"
                                    :low-images-props="{
                                        class: 'absolute-left w-100 h-100',
                                        type: 'ALL',
                                        tag: 'VImg',
                                        contain: true,
                                        disableDropShadow: true
                                    }"
                                ></ProductsLayeredImage>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Thumbs -->
            <div
                class="
                    pa-4
                    d-flex
                    flex-shrink-0
                    flex-grow-0
                    justify-center
                    no-user-select
                "
            >
                <ProductMainImageThumbs
                    :product-images="productImages"
                    :selected-image-uid="currentSkuUid"
                    item-class="carousel-v-scroll-item"
                    :type="isMobile ? 'dots' : 'thumbs'"
                    :image-stack="imageStack"
                    orientation="horizontal"
                    selector-prefix="#prod-img"
                    :disabled="isZoomed"
                    @on-change="onImgChange"
                ></ProductMainImageThumbs>
            </div>
        </div>
    </v-dialog>
</template>

<script lang="ts" setup>
    import type {
        FullImage,
        FullImageStack
    } from '~/types';
    import { mdiCloseCircle } from '@mdi/js';
    import { useIsMobile } from '~/composables';
    import ProductsLayeredImage from '~/components/products/ProductsLayeredImage/ProductsLayeredImage.vue';
    import ProductMainImageThumbs from '~/components/products/ProductMainImageThumbs/ProductMainImageThumbs.vue';
    import { useIntersectionObserver } from '@vueuse/core';

    const props = defineProps<{
        modelValue: boolean;
        selectedImageUid: Nullable<string>;
        imageObject?: Nilish<FullImage>;
        productImages: Nullable<FullImage[]>;
        itemClass?: Nilish<string>;
        imageStack?: Nilish<FullImageStack>;
        type: 'thumbs' | 'dots';
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
        'on-exit': [value: void];
    }>();

    const currentSkuUid = ref<Nullable<string>>(props.selectedImageUid);
    const imgObj = ref(props.imageObject);

    const isZoomed = ref(false);
    const { isMobile } = useIsMobile();
    const elProdImgModalOuter = shallowRef<HTMLElement>();
    const elProdImgModalItems = ref<HTMLElement[]>([]);
    const modalWidth = ref<string>('auto');


    const {
        stop: itemsObsStop
    } = useIntersectionObserver(
        elProdImgModalItems,
        onIntersect,
        {
            root: elProdImgModalOuter,
            immediate: true,
            threshold: 0.1
        }
    );

    function onIntersect(entries: IntersectionObserverEntry[]): void {
        const entry = entries[0];
        if (entry && entry.target instanceof HTMLElement && entry.isIntersecting) {
            const val = entry.target.dataset['attrVal'] ?? null;
            currentSkuUid.value = val;
        }
    }

    const unWatchSelectedSku = watch(
        () => props.selectedImageUid,
        (newVal) => {
            currentSkuUid.value = newVal;
        },
        {
            immediate: true
        }
    );

    const unWatchImgObjProp = watch(
        () => props.imageObject,
        (newVal) => {
            imgObj.value = newVal;
        },
        {
            immediate: true
        }
    );

    function setWidth(): void {
        const w: string | number = elProdImgModalOuter.value?.clientWidth ?? '0';
        modalWidth.value = `${w}px`;
    }

    function onImgChange(imgIdSel: string): void {
        const target = elProdImgModalOuter.value?.querySelector(imgIdSel);

        if (!target || !(target instanceof HTMLElement)) {
            return;
        }

        currentSkuUid.value = target.dataset['attrVal'] ?? null;

        target.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'nearest'
        });
    }

    function onClose() {
        emit('update:modelValue', false);
        emit('on-exit');
    }

    onMounted(() => {
        if (props.modelValue && currentSkuUid.value) {
            nextTick(() => {
                onImgChange(`#prod-img-${currentSkuUid.value}`);
            });
        }
    });

    onBeforeUnmount(() => {
        unWatchSelectedSku();
        unWatchImgObjProp();
        itemsObsStop();
    });
</script>

<style lang="scss">
.products-image-modal {
    position: relative;

    .dialog-close {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 1;

        .v-theme--light & {
            filter: invert(1);
        }
    }
}
</style>
